import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import {
  getInventoryDetailsDownload,
  getReportJobStatus,
  submitReportJob,
} from '../../../utils/service-calls/store-data-validation';
import { ButtonBlack } from '../../reusable';

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

const REPORT_DEFS = [
  {
    description: 'GTINs at the store that GPPS has no selling price for.',
    key: 'MISSING_PRICE',
    label: 'Missing Price in GPPS',
    scope: 'store',
    sync: false,
  },
  {
    description: 'GTINs in the country that GPCR has no product record for.',
    key: 'MISSING_PRODUCT',
    label: 'Missing Product in GPCR',
    scope: 'country',
    sync: false,
  },
  {
    description: 'Current inventory-details report for a country.',
    key: 'INVENTORY_DETAILS',
    label: 'Inventory Details',
    scope: 'country',
    sync: true,
  },
];

const extractErrorMessage = (err) => {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (err.message) {
    const inner = Array.isArray(err.errors) && err.errors[0]?.message;
    return inner && inner !== err.message ? `${err.message} — ${inner}` : err.message;
  }
  if (Array.isArray(err.errors) && err.errors[0]?.message) return err.errors[0].message;
  try {
    return JSON.stringify(err);
  } catch (_) {
    return 'Unknown error';
  }
};

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const formatTimestamp = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString();
};

const ReportPreviewContent = ({ label, url }) => (
  <iframe
    className="sdv-report-preview-frame"
    src={url}
    title={`${label} report preview`}
  />
);

ReportPreviewContent.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
};

const ReportCard = ({
  accessToken, country, def, isActive, onDownloadChange, onView, storeId,
}) => {
  const [phase, setPhase] = useState('idle');
  const [download, setDownload] = useState(null);
  const [error, setError] = useState('');

  const pollTimerRef = useRef(null);
  const pollStartedAtRef = useRef(null);
  const generationRef = useRef(0);
  const onDownloadChangeRef = useRef(onDownloadChange);

  useEffect(() => {
    onDownloadChangeRef.current = onDownloadChange;
  }, [onDownloadChange]);

  const scopeValue = def.scope === 'store' ? storeId : country;
  const hasScope = Boolean(scopeValue);

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const resetState = useCallback(() => {
    generationRef.current += 1;
    clearPollTimer();
    pollStartedAtRef.current = null;
    setPhase('idle');
    setDownload(null);
    setError('');
    if (onDownloadChangeRef.current) {
      onDownloadChangeRef.current(def.key, null);
    }
  }, [def.key]);

  useEffect(() => {
    resetState();
  }, [scopeValue, resetState]);

  useEffect(() => () => {
    generationRef.current += 1;
    clearPollTimer();
  }, []);

  const applyDownload = useCallback((nextDownload) => {
    setDownload(nextDownload);
    if (onDownloadChangeRef.current) {
      onDownloadChangeRef.current(def.key, nextDownload);
    }
  }, [def.key]);

  const pollOnce = useCallback((id, gen) => {
    getReportJobStatus(accessToken, id)
      .then((res) => {
        if (gen !== generationRef.current) return null;
        const body = res.body || {};
        if (body.status === 'COMPLETED') {
          if (body.download) {
            applyDownload(body.download);
            setPhase('completed');
          } else if (body.error) {
            setError(extractErrorMessage(body.error));
            setPhase('error');
          } else {
            setError('Report completed but no artifact was returned.');
            setPhase('error');
          }
          return null;
        }
        if (
          pollStartedAtRef.current
          && Date.now() - pollStartedAtRef.current > POLL_TIMEOUT_MS
        ) {
          setError('Timed out waiting for the report to complete. Try again later.');
          setPhase('error');
          return null;
        }
        pollTimerRef.current = setTimeout(() => pollOnce(id, gen), POLL_INTERVAL_MS);
        return null;
      })
      .catch((err) => {
        if (gen !== generationRef.current) return;
        setError(extractErrorMessage(err));
        setPhase('error');
      });
  }, [accessToken, applyDownload]);

  const handleGenerate = useCallback(() => {
    if (!hasScope) return;
    generationRef.current += 1;
    const gen = generationRef.current;
    clearPollTimer();
    applyDownload(null);
    setError('');
    setPhase('submitting');

    if (def.sync) {
      getInventoryDetailsDownload(accessToken, country)
        .then((res) => {
          if (gen !== generationRef.current) return null;
          const body = res.body || {};
          if (body.download) {
            applyDownload(body.download);
            setPhase('completed');
          } else {
            setError('Inventory details response did not include a download.');
            setPhase('error');
          }
          return null;
        })
        .catch((err) => {
          if (gen !== generationRef.current) return;
          setError(extractErrorMessage(err));
          setPhase('error');
        });
      return;
    }

    const payload = { reportType: def.key };
    if (def.scope === 'store') payload.storeId = storeId;
    else payload.country = country;

    submitReportJob(accessToken, payload)
      .then((res) => {
        if (gen !== generationRef.current) return null;
        const body = res.body || {};
        if (body.id) {
          setPhase('polling');
          pollStartedAtRef.current = Date.now();
          pollOnce(body.id, gen);
        } else {
          setError('Submit response did not include a job id.');
          setPhase('error');
        }
        return null;
      })
      .catch((err) => {
        if (gen !== generationRef.current) return;
        setError(extractErrorMessage(err));
        setPhase('error');
      });
  }, [accessToken, applyDownload, country, def, hasScope, pollOnce, storeId]);

  const handleViewClick = useCallback(() => {
    if (!download) return;
    onView(def, download);
  }, [def, download, onView]);

  const isWorking = phase === 'submitting' || phase === 'polling';
  const isCompleted = phase === 'completed' && Boolean(download);

  return (
    <article className={`sdv-report-card ${isActive ? 'sdv-report-card-active' : ''}`}>
      <header className="sdv-report-card-header">
        <div>
          <h3 className="sdv-report-card-title">{def.label}</h3>
          <p className="sdv-report-card-description">{def.description}</p>
        </div>
      </header>
      <div className="sdv-report-card-actions">
        {isWorking && (
          <div aria-label="Loading report" className="sdv-report-card-loader">
            <Loading />
          </div>
        )}
        {!isWorking && isCompleted && (
          <ButtonBlack
            label={isActive ? 'Hide' : 'View'}
            onClick={handleViewClick}
          />
        )}
        {!isWorking && !isCompleted && (
          <ButtonBlack
            isDisabled={!hasScope}
            label="Get"
            onClick={handleGenerate}
          />
        )}
      </div>
      {phase === 'error' && error && (
        <p className="sdv-status-message sdv-status-error">{error}</p>
      )}
    </article>
  );
};

ReportCard.defaultProps = {
  country: null,
  storeId: null,
};

ReportCard.propTypes = {
  accessToken: PropTypes.string.isRequired,
  country: PropTypes.string,
  def: PropTypes.shape({
    description: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    scope: PropTypes.oneOf(['store', 'country']).isRequired,
    sync: PropTypes.bool.isRequired,
  }).isRequired,
  isActive: PropTypes.bool.isRequired,
  onDownloadChange: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  storeId: PropTypes.string,
};

const ReportsTab = ({ accessToken, selectedCountry, selectedStore }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activePreview, setActivePreview] = useState(null);

  const rawCountry = selectedCountry?.value || (typeof selectedCountry === 'string' ? selectedCountry : null);
  const country = rawCountry ? rawCountry.toUpperCase() : null;
  const storeId = selectedStore?.id || null;

  useEffect(() => {
    setActivePreview(null);
  }, [country, storeId]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleView = useCallback((def, download) => {
    if (!download?.url) return;
    setActivePreview((prev) => {
      if (prev && prev.key === def.key) return null;
      return {
        download,
        key: def.key,
        label: def.label,
      };
    });
    setIsExpanded(true);
  }, []);

  const handleDownloadChange = useCallback((key, download) => {
    setActivePreview((prev) => {
      if (!prev || prev.key !== key) return prev;
      if (!download) return null;
      if (prev.download?.url === download.url) return prev;
      return { ...prev, download };
    });
  }, []);

  const closePreview = useCallback(() => setActivePreview(null), []);

  const previewDownload = activePreview?.download;
  const previewSize = previewDownload ? formatBytes(previewDownload.sizeBytes) : null;
  const previewLastModified = previewDownload
    ? formatTimestamp(previewDownload.fileLastModified)
    : null;

  return (
    <section className={`sdv-card ${isExpanded ? '' : 'sdv-card-collapsed'}`}>
      <header className="sdv-card-header">
        <button
          aria-expanded={isExpanded}
          className="sdv-card-toggle"
          type="button"
          onClick={toggleExpanded}
        >
          <span aria-hidden="true" className={`sdv-card-chevron ${isExpanded ? 'sdv-card-chevron-open' : ''}`}>▸</span>
          <h2 className="sdv-card-title">Reports</h2>
        </button>
      </header>
      {isExpanded && (
        <div className="sdv-card-body">
          <p className="sdv-card-hint">
            Generate operational reports for the selected store or country.
          </p>
          <div className="sdv-report-grid">
            {REPORT_DEFS.map((def) => (
              <ReportCard
                key={def.key}
                accessToken={accessToken}
                country={country}
                def={def}
                isActive={activePreview?.key === def.key}
                storeId={storeId}
                onDownloadChange={handleDownloadChange}
                onView={handleView}
              />
            ))}
          </div>
          {activePreview && previewDownload?.url && (
            <div className="sdv-report-preview">
              <div className="sdv-report-preview-header">
                <div className="sdv-report-preview-heading">
                  <span className="sdv-report-preview-eyebrow">Report contents</span>
                  <span className="sdv-report-preview-title">{activePreview.label}</span>
                  {(previewSize || previewLastModified) && (
                    <span className="sdv-report-preview-meta">
                      {previewSize && <span>{previewSize}</span>}
                      {previewSize && previewLastModified && <span aria-hidden="true"> · </span>}
                      {previewLastModified && <span>{`Updated ${previewLastModified}`}</span>}
                    </span>
                  )}
                </div>
                <div className="sdv-report-preview-actions">
                  <button
                    aria-label="Close preview"
                    className="sdv-report-preview-close"
                    type="button"
                    onClick={closePreview}
                  >
                    ×
                  </button>
                </div>
              </div>
              <ReportPreviewContent
                label={activePreview.label}
                url={previewDownload.url}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
};

ReportsTab.defaultProps = {
  selectedCountry: null,
  selectedStore: null,
};

ReportsTab.propTypes = {
  accessToken: PropTypes.string.isRequired,
  selectedCountry: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ]),
  selectedStore: PropTypes.shape({
    id: PropTypes.string,
  }),
};

export default ReportsTab;
